<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('weeklyCalendar.title') }}</span>
          <div class="header-actions">
            <el-dropdown @command="handleExportCommand" trigger="click">
              <el-button type="success" class="export-btn">
                <el-icon><Download /></el-icon>
                {{ $t('common.export') }}
                <el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="xlsx">
                    <el-icon><Document /></el-icon>
                    {{ $t('common.exportExcel') }}
                  </el-dropdown-item>
                  <el-dropdown-item command="csv">
                    <el-icon><Tickets /></el-icon>
                    {{ $t('common.exportCSV') }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button @click="loadTemplates" class="refresh-btn">
              <el-icon><Refresh /></el-icon>
              {{ $t('weeklyCalendar.retry') }}
            </el-button>
          </div>
        </div>
      </template>

    <!-- Days List -->
    <div class="days-list" v-loading="loading">
      <div 
        v-for="(day, index) in weekDays" 
        :key="day.value"
        class="day-card"
      >
        <!-- Card Header -->
        <div class="day-header">
          <div class="day-info">
            <span class="day-name">{{ $t(`weeklyCalendar.weekDays.${day.key}`) }}</span>
            <el-tag 
              :type="getDayTemplate(index) ? 'success' : 'info'"
              size="small"
              class="status-tag"
            >
              {{ getDayTemplate(index) ? $t('weeklyCalendar.enabled') : $t('weeklyCalendar.notConfigured') }}
            </el-tag>
          </div>
          <div class="day-actions">
            <el-button 
              v-if="!getDayTemplate(index)"
              type="primary" 
              size="small"
              @click="openEditDialog(index)"
            >
              {{ $t('weeklyCalendar.actions.add') }}
            </el-button>
            <template v-else>
              <el-button 
                type="danger" 
                size="small"
                @click="deleteTemplate(index)"
              >
                {{ $t('weeklyCalendar.actions.delete') }}
              </el-button>
              <el-button 
                type="primary" 
                size="small"
                @click="openEditDialog(index)"
              >
                {{ $t('weeklyCalendar.actions.edit') }}
              </el-button>
            </template>
          </div>
        </div>

        <!-- Card Content -->
        <div v-if="getDayTemplate(index)" class="day-content">
          <div class="info-row">
            <span class="info-label">{{ $t('weeklyCalendar.dialog.enabled') }}:</span>
            <span class="info-value">
              {{ getDayTemplate(index).isEnabled ? $t('weeklyCalendar.dialog.yes') : $t('weeklyCalendar.dialog.no') }}
            </span>
          </div>
          <div v-if="getDayTemplate(index).isEnabled && getDayTemplate(index).availableHours?.length" class="info-row">
            <span class="info-label">{{ $t('weeklyCalendar.availableHours') }}:</span>
            <span class="info-value hours-list">
              {{ formatHours(getDayTemplate(index).availableHours) }}
            </span>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="day-empty">
          <span class="empty-text">{{ $t('weeklyCalendar.noTemplate') }}</span>
        </div>
      </div>
    </div>
    </el-card>

    <!-- Edit Dialog -->
    <el-dialog 
      v-model="showDialog"
      :title="$t('weeklyCalendar.dialog.title')"
      width="500px"
      :close-on-click-modal="false"
      class="edit-template-dialog"
    >
      <el-form :model="editForm" label-width="100px">
        <!-- Day Name -->
        <el-form-item :label="$t('weeklyCalendar.dialog.dayName')">
          <el-input 
            v-model="editForm.dayName"
            disabled
          />
        </el-form-item>

        <!-- Enabled Toggle -->
        <el-form-item :label="$t('weeklyCalendar.dialog.enabled')">
          <el-switch 
            v-model="editForm.enabled"
            :active-text="$t('weeklyCalendar.dialog.yes')"
            :inactive-text="$t('weeklyCalendar.dialog.no')"
          />
        </el-form-item>

        <!-- Available Hours -->
        <el-form-item :label="$t('weeklyCalendar.dialog.availableHours')" v-if="editForm.enabled">
          <div class="hours-selector">
            <div class="hours-header">
              <span class="selected-count">{{ $t('weeklyCalendar.dialog.selectedCount', { count: editForm.availableHours.length }) }}</span>
              <div class="hours-actions">
                <el-button size="small" @click="selectAllHours">{{ $t('weeklyCalendar.dialog.selectAll') }}</el-button>
                <el-button size="small" @click="clearHours">{{ $t('weeklyCalendar.dialog.clear') }}</el-button>
              </div>
            </div>
            <div class="hours-grid">
              <el-button
                v-for="hour in allHours"
                :key="hour"
                :type="editForm.availableHours.includes(hour) ? 'primary' : ''"
                size="small"
                class="hour-btn"
                @click="toggleHour(hour)"
              >
                {{ hour }}
              </el-button>
            </div>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showDialog = false">{{ $t('weeklyCalendar.dialog.cancel') }}</el-button>
          <el-button type="primary" @click="saveTemplate" :loading="saving">
            {{ saving ? $t('weeklyCalendar.dialog.saving') : $t('weeklyCalendar.dialog.save') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh, Download, ArrowDown, Document, Tickets } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'
import { useScheduleStore } from '@/stores/schedule'
import { exportToExcel, exportToCSV } from '@/utils/export'

const { t } = useI18n()
const scheduleStore = useScheduleStore()

const loading = computed(() => scheduleStore.loading)
const saving = ref(false)
const showDialog = ref(false)
const currentIndex = ref(null)

const weekDays = [
  { key: 'sunday', value: 0 },
  { key: 'monday', value: 1 },
  { key: 'tuesday', value: 2 },
  { key: 'wednesday', value: 3 },
  { key: 'thursday', value: 4 },
  { key: 'friday', value: 5 },
  { key: 'saturday', value: 6 }
]

const allHours = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'
]

// Use store's weeklyCalendarTemplates
const templates = computed(() => scheduleStore.weeklyCalendarTemplates)

const editForm = reactive({
  dayName: '',
  enabled: true,
  availableHours: []
})

const getDayTemplate = (dayIndex) => {
  return templates.value.find(t => t.dayOfWeek === dayIndex)
}

const formatHours = (hours) => {
  if (!hours || hours.length === 0) return ''
  // Convert integer hours to formatted strings
  return hours.map(h => `${h.toString().padStart(2, '0')}:00`).join(', ')
}

const toggleHour = (hour) => {
  const index = editForm.availableHours.indexOf(hour)
  if (index > -1) {
    editForm.availableHours.splice(index, 1)
  } else {
    editForm.availableHours.push(hour)
  }
}

const selectAllHours = () => {
  editForm.availableHours = [...allHours]
}

const clearHours = () => {
  editForm.availableHours = []
}

const loadTemplates = async () => {
  try {
    await scheduleStore.fetchWeeklyCalendarTemplates()
  } catch (error) {
    console.error('Failed to load templates:', error)
    ElMessage.error(t('weeklyCalendar.confirm.saveFailed'))
  }
}

const openEditDialog = (dayIndex) => {
  currentIndex.value = dayIndex
  const template = getDayTemplate(dayIndex)
  
  editForm.dayName = t(`weeklyCalendar.weekDays.${weekDays[dayIndex].key}`)
  editForm.enabled = template ? template.isEnabled : true
  // Convert integer hours to string format for display
  editForm.availableHours = template?.availableHours ? 
    template.availableHours.map(h => `${h.toString().padStart(2, '0')}:00`) : []
  
  showDialog.value = true
}

const saveTemplate = async () => {
  if (!editForm.enabled && editForm.availableHours.length === 0) {
    ElMessage.warning(t('weeklyCalendar.noHours'))
    return
  }
  
  saving.value = true
  try {
    const templateData = {
      dayOfWeek: currentIndex.value,
      dayName: editForm.dayName,
      availableHours: editForm.availableHours.map(h => parseInt(h.split(':')[0])),
      isEnabled: editForm.enabled
    }
    
    const existingTemplate = getDayTemplate(currentIndex.value)
    
    let result
    if (existingTemplate) {
      // Update existing template
      result = await scheduleStore.updateWeeklyCalendarTemplateAction(currentIndex.value, templateData)
    } else {
      // Create new template
      result = await scheduleStore.saveWeeklyCalendarTemplate(templateData)
    }
    
    if (result.success) {
      ElMessage.success(t('weeklyCalendar.confirm.saveSuccess'))
      showDialog.value = false
    } else {
      ElMessage.error(result.message || t('weeklyCalendar.confirm.saveFailed'))
    }
  } catch (error) {
    console.error('Failed to save template:', error)
    ElMessage.error(t('weeklyCalendar.confirm.saveFailed'))
  } finally {
    saving.value = false
  }
}

const deleteTemplate = async (dayIndex) => {
  try {
    await ElMessageBox.confirm(
      t('weeklyCalendar.confirmDelete'),
      t('common.confirmDelete'),
      {
        confirmButtonText: t('weeklyCalendar.actions.delete'),
        cancelButtonText: t('weeklyCalendar.dialog.cancel'),
        type: 'warning'
      }
    )
    
    const result = await scheduleStore.removeWeeklyCalendarTemplate(dayIndex)
    
    if (result.success) {
      ElMessage.success(t('weeklyCalendar.confirm.deleteSuccess'))
    } else {
      ElMessage.error(result.message || t('weeklyCalendar.confirm.deleteFailed'))
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Failed to delete template:', error)
      ElMessage.error(t('weeklyCalendar.confirm.deleteFailed'))
    }
  }
}

// Export functionality
const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = (format = 'xlsx') => {
  try {
    const templates = scheduleStore.weeklyCalendarTemplates
    
    if (!templates || templates.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Transform data for export
    const exportData = templates.map(template => ({
      'Day of Week': t(`weeklyCalendar.weekDays.${weekDays[template.dayOfWeek].key}`),
      'Enabled': template.isEnabled ? t('weeklyCalendar.dialog.yes') : t('weeklyCalendar.dialog.no'),
      'Available Hours': template.availableHours?.length > 0 
        ? formatHours(template.availableHours) 
        : t('weeklyCalendar.noHours')
    }))
    
    // Export based on format
    if (format === 'csv') {
      exportToCSV(exportData, 'weekly-calendar-templates')
    } else {
      exportToExcel(exportData, 'weekly-calendar-templates', 'Weekly Calendar Templates')
    }
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  }
}

onMounted(() => {
  loadTemplates()
})
</script>

<style scoped lang="scss">
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  .header-actions {
    display: flex;
    gap: 12px;
    align-items: center;

    .export-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      transition: all 0.3s ease;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(103, 194, 58, 0.2);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(103, 194, 58, 0.3);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .refresh-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      transition: all 0.3s ease;
      border-radius: 8px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
      }

      &:active {
        transform: translateY(0);
        box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
      }

      .el-icon {
        transition: transform 0.3s ease;
      }

      &:hover .el-icon {
        animation: rotate-refresh 0.6s ease-in-out;
      }
    }
  }
}

@keyframes rotate-refresh {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.days-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  .day-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    }

    .day-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid #f0f2f5;

        .day-info {
          display: flex;
          align-items: center;
          gap: 12px;

          .day-name {
            font-size: 16px;
            font-weight: 600;
            color: #303133;
          }

          .status-tag {
            font-size: 12px;
            padding: 2px 8px;
          }
        }

        .day-actions {
          display: flex;
          gap: 8px;

          .el-button {
            height: 32px;
            padding: 0 16px;
            font-size: 13px;
          }
        }
      }

      .day-content {
        .info-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
          font-size: 14px;
          line-height: 1.6;

          &:last-child {
            margin-bottom: 0;
          }

          .info-label {
            font-weight: 500;
            color: #606266;
            min-width: 100px;
            flex-shrink: 0;
          }

          .info-value {
            color: #303133;
            flex: 1;

            &.hours-list {
              display: inline-block;
              max-width: 600px;
              word-wrap: break-word;
            }
          }
        }
      }

      .day-empty {
        padding: 20px 0;
        text-align: center;
        color: #909399;
        font-size: 14px;
        font-style: italic;

        .empty-text {
          display: block;
        }
      }
    }
  }

.edit-template-dialog {
  :deep(.el-dialog__header) {
      padding: 20px 24px;
      border-bottom: 1px solid #f0f2f5;

      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }
    }

    :deep(.el-dialog__body) {
      padding: 24px;
    }

    .hours-selector {
      width: 100%;

      .hours-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #f0f2f5;

        .selected-count {
          font-size: 14px;
          font-weight: 500;
          color: #606266;
        }

        .hours-actions {
          display: flex;
          gap: 8px;

          .el-button {
            height: 28px;
            padding: 0 12px;
            font-size: 12px;
          }
        }
      }

      .hours-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;

        .hour-btn {
          height: 40px;
          font-size: 14px;
          font-weight: 500;
          border-radius: 6px;
          transition: all 0.3s ease;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          border: 2px solid transparent !important;

          // Unselected state
          &:not([class*='is-']) {
            background-color: #f5f7fa !important;
            border-color: #e4e7ed !important;
            color: #606266 !important;

            &:hover {
              border-color: #409eff !important;
              color: #409eff !important;
              background-color: #ecf5ff !important;
              transform: translateY(-2px);
              box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
            }

            &:active {
              transform: translateY(0);
            }
          }

          // Selected state (primary)
          &.el-button--primary {
            background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%) !important;
            border-color: #409eff !important;
            color: #ffffff !important;
            font-weight: 600;
            box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);

            &:hover {
              background: linear-gradient(135deg, #66b1ff 0%, #409eff 100%) !important;
              box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
              transform: translateY(-2px);
            }

            &:active {
              transform: translateY(0);
              box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
            }
          }
        }
      }
    }

    :deep(.el-dialog__footer) {
      padding: 16px 24px;
      border-top: 1px solid #f0f2f5;
      display: flex;
      gap: 12px;
      justify-content: flex-end;

      .el-button {
        flex: 0 0 120px;
        height: 40px;
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
</style>
